import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";

const LOGO = "/checkin-tarife-logo.png";
const WHATSAPP_URL = "https://wa.me/491772356898";
const PHONE_TEL = "tel:+491772356898";
const PHONE_DISPLAY = "+49 177 2356898";

const PRODUCTS = [
  { key: "strom", label: "Strom", dot: "dot-strom" },
  { key: "gas", label: "Gas", dot: "dot-gas" },
  { key: "internet", label: "Internet", dot: "dot-internet" },
  { key: "mobilfunk", label: "Mobilfunk", dot: "dot-mobile" },
];

const REQUIRED = {
  strom: ["strom_plz", "strom_anbieter", "strom_grundpreis", "strom_arbeitspreis"],
  gas: ["gas_plz", "gas_anbieter", "gas_grundpreis", "gas_arbeitspreis"],
  internet: ["internet_plz", "internet_adresse", "internet_anbieter", "internet_preis", "internet_speed", "internet_technik"],
  mobilfunk: ["mobile_preis", "mobile_daten", "mobile_netz"],
};

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-visible"); obs.unobserve(e.target); }
      }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

const WHATSAPP_LABEL = "WhatsApp (externer Dienst)";

export default function Landing() {
  useReveal();
  const [product, setProduct] = useState("strom");
  const [form, setForm] = useState({ consent: false });
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [config, setConfig] = useState({ email_enabled: false, whatsapp_enabled: false, callback_enabled: false });
  const [cb, setCb] = useState({ consent: false });
  const [cbMsg, setCbMsg] = useState("");
  const [cbBusy, setCbBusy] = useState(false);
  const [cbDone, setCbDone] = useState(false);
  const preCheckRef = useRef(null);
  const navigate = useNavigate();
  const logoClicks = useRef(0);
  const logoTimer = useRef(null);

  const handleLogoClick = (e) => {
    if (e) e.preventDefault();
    logoClicks.current += 1;
    clearTimeout(logoTimer.current);
    logoTimer.current = setTimeout(() => { logoClicks.current = 0; }, 900);
    if (logoClicks.current >= 3) {
      logoClicks.current = 0;
      clearTimeout(logoTimer.current);
      navigate("/admin/login");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const val = (name) => form[name] ?? "";

  const setCbField = (k, v) => setCb((f) => ({ ...f, [k]: v }));
  const submitCallback = async (e) => {
    e.preventDefault();
    if (!String(cb.name || "").trim() || !String(cb.phone || "").trim() || !String(cb.preferred_time || "").trim()) {
      setCbMsg("Bitte Name, Telefon und Kontaktzeit angeben.");
      return;
    }
    if (!cb.consent) { setCbMsg("Bitte stimmen Sie der Verarbeitung Ihrer Angaben zu."); return; }
    setCbMsg("");
    setCbBusy(true);
    try {
      await api.post("/callback", {
        name: cb.name, phone: cb.phone, email: cb.email || "",
        preferred_time: cb.preferred_time, topic: cb.topic || "", consent: !!cb.consent, company: cb.company_hp || "",
      });
      setCbDone(true);
    } catch (err) {
      setCbMsg(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCbBusy(false);
    }
  };

  const openCheck = (p) => {
    setProduct(p);
    setResult(null);
    setMessage("");
    setMenuOpen(false);
    setTimeout(() => preCheckRef.current?.scrollIntoView({ behavior: "smooth" }), 40);
  };

  const buildValues = () => {
    if (product === "strom")
      return { plz: val("strom_plz"), anbieter: val("strom_anbieter"), verbrauch: val("strom_verbrauch"),
        haushalt: val("strom_haushalt"), grundpreis: val("strom_grundpreis"), arbeitspreis: val("strom_arbeitspreis"), abschlag: val("strom_abschlag") };
    if (product === "gas")
      return { plz: val("gas_plz"), anbieter: val("gas_anbieter"), verbrauch: val("gas_verbrauch"),
        wohnflaeche: val("gas_wohnflaeche"), grundpreis: val("gas_grundpreis"), arbeitspreis: val("gas_arbeitspreis"), abschlag: val("gas_abschlag") };
    if (product === "internet")
      return { plz: val("internet_plz"), adresse: val("internet_adresse"), anbieter: val("internet_anbieter"),
        preis: val("internet_preis"), speed: val("internet_speed"), technik: val("internet_technik"), ende: val("internet_ende") };
    return { preis: val("mobile_preis"), daten: val("mobile_daten"), netz: val("mobile_netz"),
      anzahl: val("mobile_anzahl"), ende: val("mobile_ende"), phone: val("mobile_phone") };
  };

  const submit = async (e) => {
    e.preventDefault();
    const contactReq = ["kontakt_name", "kontakt_telefon", "kontakt_email", "kontakt_plz", "kontakt_zeit"];
    const missing = [...REQUIRED[product], ...contactReq].filter((n) => !String(form[n] || "").trim());
    if (!form.consent) missing.push("consent");
    if (missing.length) {
      setMessage("Bitte füllen Sie alle Pflichtangaben aus und stimmen Sie der Verarbeitung zu.");
      return;
    }
    setMessage("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/precheck", {
        product,
        values: buildValues(),
        contact: {
          name: val("kontakt_name"), phone: val("kontakt_telefon"), email: val("kontakt_email"),
          postcode: val("kontakt_plz"), preferred_time: val("kontakt_zeit"),
        },
        consent: !!form.consent,
        company: val("company_hp"),
      });
      setResult(data.result);
      setTimeout(() => document.querySelector("#result-card")?.scrollIntoView({ behavior: "smooth", block: "center" }), 40);
    } catch (err) {
      setMessage(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inp = (name, props = {}) => (
    <input value={val(name)} onChange={(e) => set(name, e.target.value)} data-testid={`field-${name}`} {...props} />
  );
  const sel = (name, children, props = {}) => (
    <select value={val(name)} onChange={(e) => set(name, e.target.value)} data-testid={`field-${name}`} {...props}>{children}</select>
  );

  return (
    <>
      <div className="test-banner" data-testid="test-banner">
        <div className="shell">
          <span>⚠ Testbetrieb – Orientierungswerte sind gekennzeichnete Testwerte, keine Live-Marktdaten.</span>
        </div>
      </div>

      <header className="site-header" id="top">
        <div className="shell nav-shell">
          <span className="brand" role="button" tabIndex={0} aria-label="Check'in Tarife Startseite" onClick={handleLogoClick}
            onKeyDown={(e) => { if (e.key === "Enter") window.scrollTo({ top: 0, behavior: "smooth" }); }}
            style={{ cursor: "pointer" }} data-testid="header-logo">
            <img src={LOGO} alt="Check'in Tarife" />
          </span>
          <button className="menu-toggle" type="button" aria-expanded={menuOpen} aria-controls="main-nav"
            onClick={() => setMenuOpen((v) => !v)} data-testid="menu-toggle">
            <span></span><span></span><span></span><span className="sr-only">Menü öffnen</span>
          </button>
          <nav className={`main-nav${menuOpen ? " is-open" : ""}`} id="main-nav" aria-label="Hauptnavigation">
            <a href="#tarifwelten" onClick={() => setMenuOpen(false)}>Tarifwelten</a>
            <a href="#so-gehts" onClick={() => setMenuOpen(false)}>So geht's</a>
            <a href="#gewerbe" onClick={() => setMenuOpen(false)}>Gewerbekunden</a>
            <a href="#kontakt" onClick={() => setMenuOpen(false)}>Kontakt</a>
          </nav>
          <button className="button button-small button-coral" type="button" onClick={() => openCheck("strom")} data-testid="header-precheck-btn">
            Pre-Check starten
          </button>
        </div>
      </header>

      <main id="main">
        {/* HERO */}
        <section className="hero section-pad">
          <div className="hero-glow hero-glow-one" aria-hidden="true"></div>
          <div className="hero-glow hero-glow-two" aria-hidden="true"></div>
          <div className="shell hero-grid">
            <div className="hero-copy reveal">
              <div className="eyebrow"><span className="status-dot"></span> Deutschlandweit für Sie da</div>
              <h1>Tarife einchecken.<br /><span>Kosten auschecken.</span></h1>
              <p className="hero-lead">
                In wenigen Minuten erfahren Sie unverbindlich, ob sich eine genauere Prüfung Ihres
                Strom-, Gas-, Internet- oder Mobilfunktarifs lohnen könnte.
              </p>
              <div className="hero-actions">
                <button className="button button-coral button-large" type="button" onClick={() => openCheck("strom")} data-testid="hero-precheck-btn">
                  Jetzt kostenlos checken <span aria-hidden="true">→</span>
                </button>
                <a className="button button-ghost button-large" href="#kontakt">Rückruf anfragen</a>
              </div>
              <div className="trust-strip" aria-label="Ihre Vorteile">
                <span>✓ Für Kunden kostenlos</span>
                <span>✓ Unverbindlich</span>
                <span>✓ Ausgewählte Anbieter</span>
              </div>
            </div>
            <div className="hero-visual reveal" aria-label="Grafische Darstellung der Tarifwelten">
              <div className="orbit orbit-one" aria-hidden="true"></div>
              <div className="orbit orbit-two" aria-hidden="true"></div>
              <div className="hero-mark" aria-hidden="true"><span className="hero-mark-pin">✓</span><small>CHECK</small></div>
              <div className="float-card float-card-strom"><span className="mini-icon">S</span><strong>Strom</strong><small>Tarif-Ampel</small></div>
              <div className="float-card float-card-gas"><span className="mini-icon">G</span><strong>Gas</strong><small>Kosten-Check</small></div>
              <div className="float-card float-card-net"><span className="mini-icon">@</span><strong>Internet</strong><small>Preis & Leistung</small></div>
              <div className="float-card float-card-mobile"><span className="mini-icon">SIM</span><strong>Mobilfunk</strong><small>Bedarf prüfen</small></div>
              <div className="visual-caption"><span></span> Ein Check. Vier Tarifwelten.</div>
            </div>
          </div>
        </section>

        {/* BENEFIT BAND */}
        <section className="benefit-band" aria-label="Serviceversprechen">
          <div className="shell benefit-grid">
            <div><strong>0 €</strong><span>Kosten für Kunden</span></div>
            <div><strong>100 %</strong><span>unverbindlicher Pre-Check</span></div>
            <div><strong>24 h</strong><span>meist bis zur Rückmeldung</span></div>
            <div><strong>DE</strong><span>deutschlandweite Beratung</span></div>
          </div>
        </section>

        {/* TARIFWELTEN */}
        <section className="section-pad services" id="tarifwelten">
          <div className="shell">
            <div className="section-heading reveal">
              <div><span className="kicker">Unsere Tarifwelten</span><h2>Was möchten Sie heute checken?</h2></div>
              <p>Wählen Sie Ihren Bereich. Der Pre-Check führt Sie kompakt zu einer ersten Einschätzung.</p>
            </div>
            <div className="service-grid">
              {[
                { p: "strom", cls: "service-strom", num: "01", sym: "S", h: "Strom", d: "Arbeitspreis, Grundpreis und Verbrauch auf einen Blick einordnen." },
                { p: "gas", cls: "service-gas", num: "02", sym: "G", h: "Gas", d: "Ihre voraussichtlichen Jahreskosten mit einer groben Preisspanne vergleichen." },
                { p: "internet", cls: "service-internet", num: "03", sym: "@", h: "Internet & Glasfaser", d: "Preis, Geschwindigkeit und Anschlussart unverbindlich einschätzen." },
                { p: "mobilfunk", cls: "service-mobile", num: "04", sym: "SIM", h: "Mobilfunk", d: "Datenvolumen, Netz und Preis passend zu Ihrem Bedarf betrachten." },
              ].map((c) => (
                <button key={c.p} className={`service-card ${c.cls} reveal`} onClick={() => openCheck(c.p)} data-testid={`service-card-${c.p}`}>
                  <div className="service-top"><span className="service-number">{c.num}</span><span className="service-symbol">{c.sym}</span></div>
                  <h3>{c.h}</h3>
                  <p>{c.d}</p>
                  <span className="text-link">{c.h.split(" ")[0]} checken <span>→</span></span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="section-pad process" id="so-gehts">
          <div className="shell process-grid">
            <div className="process-copy reveal">
              <span className="kicker">Einfach eingecheckt</span>
              <h2>Kein Tarifdschungel.<br />Nur drei klare Schritte.</h2>
              <p>Der Pre-Check ist eine erste Orientierung und noch kein konkretes Tarifangebot. Die genaue Verfügbarkeit und mögliche Ersparnis prüfen wir anschließend persönlich.</p>
              <button className="button button-navy" type="button" onClick={() => openCheck("strom")} data-testid="process-precheck-btn">Pre-Check öffnen</button>
            </div>
            <ol className="steps reveal">
              <li><span>1</span><div><strong>Tarifwelt auswählen</strong><p>Strom, Gas, Internet oder Mobilfunk.</p></div></li>
              <li><span>2</span><div><strong>Aktuelle Daten eintragen</strong><p>Nur die Angaben, die für eine erste Einschätzung wichtig sind.</p></div></li>
              <li><span>3</span><div><strong>Ergebnis einchecken</strong><p>Ampel oder Preis-Leistungs-Einschätzung sehen und Rückruf vormerken.</p></div></li>
            </ol>
          </div>
        </section>

        {/* PRE-CHECK */}
        <section className="section-pad checker-section" id="pre-check" ref={preCheckRef}>
          <div className="shell">
            <div className="checker-intro reveal">
              <div><span className="kicker kicker-light">Unverbindlicher Pre-Check</span><h2>Wie gut passt Ihr aktueller Tarif noch?</h2></div>
              <p>Diese Fassung nutzt gekennzeichnete Test-Orientierungswerte. Vor Veröffentlichung werden diese durch aktuelle, monatlich gepflegte Werte ersetzt.</p>
            </div>

            {!result && (
              <div className="checker-card reveal" id="checker-card">
                <div className="product-tabs" role="tablist" aria-label="Tarifart auswählen">
                  {PRODUCTS.map((p) => (
                    <button key={p.key} className={`product-tab${product === p.key ? " is-active" : ""}`} type="button" role="tab"
                      aria-selected={product === p.key} onClick={() => { setProduct(p.key); setMessage(""); }} data-testid={`tab-${p.key}`}>
                      <span className={`tab-dot ${p.dot}`}></span>{p.label}
                    </button>
                  ))}
                </div>

                <form className="precheck-form" onSubmit={submit} noValidate>
                  <input type="text" name="company_hp" value={val("company_hp")} onChange={(e) => set("company_hp", e.target.value)}
                    style={{ display: "none" }} tabIndex={-1} autoComplete="off" aria-hidden="true" />

                  {product === "strom" && (
                    <div className="form-panel">
                      <div className="panel-heading"><span>Strom-Check</span><small>Pflichtfelder sind mit * markiert</small></div>
                      <div className="form-grid">
                        <label>Postleitzahl *{inp("strom_plz", { inputMode: "numeric", maxLength: 5, placeholder: "z. B. 48157" })}</label>
                        <label>Aktueller Anbieter *{inp("strom_anbieter", { placeholder: "Name des Anbieters" })}</label>
                        <label>Jahresverbrauch in kWh{inp("strom_verbrauch", { type: "number", min: 1, placeholder: "z. B. 3500" })}</label>
                        <label>Haushaltsgröße{sel("strom_haushalt", <>
                          <option value="">Falls Verbrauch unbekannt</option>
                          <option value="1">1 Person</option><option value="2">2 Personen</option>
                          <option value="3">3 Personen</option><option value="4">4 Personen</option>
                          <option value="5">5 oder mehr Personen</option></>)}</label>
                        <label>Grundpreis pro Monat (€) *{inp("strom_grundpreis", { type: "text", inputMode: "decimal", placeholder: "z. B. 14,90" })}</label>
                        <label>Arbeitspreis (ct/kWh) *{inp("strom_arbeitspreis", { type: "text", inputMode: "decimal", placeholder: "z. B. 34,50" })}</label>
                        <label>Monatlicher Abschlag (€){inp("strom_abschlag", { type: "text", inputMode: "decimal", placeholder: "Optional zur Schätzung" })}</label>
                      </div>
                      <p className="field-hint">Kein Verbrauch zur Hand? Wir schätzen über Haushaltsgröße oder – falls vorhanden – über Ihren Abschlag.</p>
                    </div>
                  )}

                  {product === "gas" && (
                    <div className="form-panel">
                      <div className="panel-heading"><span>Gas-Check</span><small>Pflichtfelder sind mit * markiert</small></div>
                      <div className="form-grid">
                        <label>Postleitzahl *{inp("gas_plz", { inputMode: "numeric", maxLength: 5, placeholder: "z. B. 48157" })}</label>
                        <label>Aktueller Anbieter *{inp("gas_anbieter", { placeholder: "Name des Anbieters" })}</label>
                        <label>Jahresverbrauch in kWh{inp("gas_verbrauch", { type: "number", min: 1, placeholder: "z. B. 18000" })}</label>
                        <label>Wohnfläche in m²{inp("gas_wohnflaeche", { type: "number", min: 1, placeholder: "Falls Verbrauch unbekannt" })}</label>
                        <label>Grundpreis pro Monat (€) *{inp("gas_grundpreis", { type: "text", inputMode: "decimal", placeholder: "z. B. 16,90" })}</label>
                        <label>Arbeitspreis (ct/kWh) *{inp("gas_arbeitspreis", { type: "text", inputMode: "decimal", placeholder: "z. B. 11,20" })}</label>
                        <label>Monatlicher Abschlag (€){inp("gas_abschlag", { type: "text", inputMode: "decimal", placeholder: "Optional zur Schätzung" })}</label>
                      </div>
                      <p className="field-hint">Wenn der Verbrauch fehlt, schätzen wir wahlweise über Wohnfläche oder Abschlag.</p>
                    </div>
                  )}

                  {product === "internet" && (
                    <div className="form-panel">
                      <div className="panel-heading"><span>Internet-Check</span><small>Allgemeine Preis-Leistungs-Einschätzung</small></div>
                      <div className="form-grid">
                        <label>Postleitzahl *{inp("internet_plz", { inputMode: "numeric", maxLength: 5, placeholder: "z. B. 48157" })}</label>
                        <label>Straße und Hausnummer *{inp("internet_adresse", { placeholder: "Ihre Anschlussadresse" })}</label>
                        <label>Aktueller Anbieter *{inp("internet_anbieter", { placeholder: "Name des Anbieters" })}</label>
                        <label>Monatlicher Preis (€) *{inp("internet_preis", { type: "text", inputMode: "decimal", placeholder: "z. B. 44,99" })}</label>
                        <label>Aktuelle Geschwindigkeit *{sel("internet_speed", <>
                          <option value="">Bitte wählen</option>
                          <option value="16">bis 16 Mbit/s</option><option value="50">bis 50 Mbit/s</option>
                          <option value="100">bis 100 Mbit/s</option><option value="250">bis 250 Mbit/s</option>
                          <option value="500">bis 500 Mbit/s</option><option value="1000">bis 1.000 Mbit/s</option></>)}</label>
                        <label>Anschlussart *{sel("internet_technik", <>
                          <option value="">Bitte wählen</option><option>DSL</option><option>Kabel</option><option>Glasfaser</option></>)}</label>
                        <label>Vertragsende (freiwillig){inp("internet_ende", { type: "date" })}</label>
                      </div>
                    </div>
                  )}

                  {product === "mobilfunk" && (
                    <div className="form-panel">
                      <div className="panel-heading"><span>Mobilfunk-Check</span><small>Allgemeine Preis-Leistungs-Einschätzung</small></div>
                      <div className="form-grid">
                        <label>Monatlicher Preis (€) *{inp("mobile_preis", { type: "text", inputMode: "decimal", placeholder: "z. B. 24,99" })}</label>
                        <label>Datenvolumen in GB *{inp("mobile_daten", { type: "number", min: 1, placeholder: "z. B. 25" })}</label>
                        <label>Bevorzugtes Netz *{sel("mobile_netz", <>
                          <option value="">Bitte wählen</option><option>Telekom-Netz</option><option>Vodafone-Netz</option>
                          <option>Telefónica/O2-Netz</option><option>Kein bevorzugtes Netz</option></>)}</label>
                        <label>Anzahl SIM-Karten (freiwillig){inp("mobile_anzahl", { type: "number", min: 1, placeholder: "z. B. 2" })}</label>
                        <label>Vertragsende (freiwillig){inp("mobile_ende", { type: "date" })}</label>
                        <label>Smartphone gewünscht (freiwillig){sel("mobile_phone", <>
                          <option value="">Keine Angabe</option><option>Ja</option><option>Nein</option><option>Noch unsicher</option></>)}</label>
                      </div>
                    </div>
                  )}

                  <fieldset className="contact-data">
                    <legend>Wohin dürfen wir Ihr Ergebnis zuordnen?</legend>
                    <p>Das Ergebnis erscheint direkt nach dem Absenden. Ihre Anfrage wird sicher gespeichert und vertraulich behandelt.</p>
                    <div className="form-grid">
                      <label>Vor- und Nachname *{inp("kontakt_name", { autoComplete: "name" })}</label>
                      <label>Telefonnummer *{inp("kontakt_telefon", { type: "tel", autoComplete: "tel" })}</label>
                      <label>E-Mail-Adresse *{inp("kontakt_email", { type: "email", autoComplete: "email" })}</label>
                      <label>Postleitzahl *{inp("kontakt_plz", { inputMode: "numeric", maxLength: 5 })}</label>
                      <label>Bevorzugte Kontaktzeit *{sel("kontakt_zeit", <>
                        <option value="">Bitte wählen</option><option>08:00–10:00 Uhr</option><option>10:00–12:00 Uhr</option>
                        <option>12:00–15:00 Uhr</option><option>15:00–18:00 Uhr</option><option>18:00–20:00 Uhr</option></>)}</label>
                    </div>
                    <label className="check-row">
                      <input type="checkbox" checked={!!form.consent} onChange={(e) => set("consent", e.target.checked)} data-testid="field-consent" />
                      <span>Ich bitte um Durchführung des unverbindlichen Pre-Checks und die Bearbeitung meiner Anfrage. Die <Link to="/datenschutz" target="_blank" rel="noopener noreferrer" data-testid="precheck-privacy-link">Datenschutzhinweise</Link> habe ich zur Kenntnis genommen. *</span>
                    </label>
                  </fieldset>

                  <div className="form-submit-row">
                    <div className="security-note"><span>✓</span><p><strong>Unverbindliche Orientierung</strong><br />Noch kein Vertragsangebot und keine Wechselverpflichtung.</p></div>
                    <button className="button button-coral button-large" type="submit" disabled={submitting} data-testid="precheck-submit-btn">
                      {submitting ? "Wird berechnet…" : <>Ergebnis anzeigen <span>→</span></>}
                    </button>
                  </div>
                  {message && <div className="form-message" role="alert" data-testid="form-message">{message}</div>}
                </form>
              </div>
            )}

            {result && (
              <div className="result-card" id="result-card" aria-live="polite" data-testid="result-card">
                <div className="result-top">
                  <div><span className="kicker">Ihr unverbindlicher Pre-Check</span><h3 data-testid="result-title">{result.title}</h3></div>
                  <div className={`traffic-light is-${result.level}`} data-testid={`traffic-light-${result.level}`} aria-label="Tarifampel"><span></span><span></span><span></span></div>
                </div>
                <div className="result-stats">
                  {result.stats.map((s, i) => (
                    <div className="result-stat" key={i}><small>{s.label}</small><strong>{s.value}</strong></div>
                  ))}
                </div>
                <div className="result-copy" dangerouslySetInnerHTML={{ __html: result.copy }} />
                <div className="result-actions">
                  <a className="button button-coral" href="#kontakt" data-testid="result-callback-btn">Rückruf anfragen</a>
                  <button className="button button-ghost" type="button" onClick={() => { setResult(null); setTimeout(() => document.querySelector("#checker-card")?.scrollIntoView({ behavior: "smooth", block: "start" }), 40); }} data-testid="result-reset-btn">Angaben ändern</button>
                </div>
                <p className="result-disclaimer">
                  Testberechnung mit groben, nicht live angebundenen Orientierungswerten. Das Ergebnis ist unverbindlich, stellt kein Tarifangebot dar und ersetzt keine individuelle Prüfung.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* BUSINESS */}
        <section className="section-pad business" id="gewerbe">
          <div className="business-shape business-shape-one" aria-hidden="true"></div>
          <div className="business-shape business-shape-two" aria-hidden="true"></div>
          <div className="shell business-grid">
            <div className="reveal">
              <span className="kicker kicker-light">Für Gewerbekunden</span>
              <h2>Ihr Bedarf ist individueller.<br />Unser Gespräch auch.</h2>
              <p>Für Unternehmen verzichten wir bewusst auf pauschale Ampeln. Wir prüfen Energie- und Telekommunikationsbedarf persönlich und berücksichtigen Standorte, Nutzung und Vertragsstruktur.</p>
              <a className="button button-light" href="#kontakt">Gewerblichen Rückruf anfragen</a>
            </div>
            <div className="business-card reveal">
              <span className="business-badge">B2B</span>
              <h3>Direkt statt pauschal</h3>
              <ul>
                <li>Persönliche Bedarfsklärung</li>
                <li>Ausgewählte Anbieter</li>
                <li>Deutschlandweite Beratung</li>
                <li>Meist Rückmeldung innerhalb von 24 Stunden</li>
              </ul>
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section className="section-pad contact" id="kontakt">
          <div className="shell contact-grid">
            <div className="contact-copy reveal">
              <span className="kicker">Kontakt & Rückruf</span>
              <h2>Bereit für den nächsten Check?</h2>
              <p>Hinterlassen Sie Ihre Kontaktdaten – wir melden uns zur gewünschten Zeit persönlich bei Ihnen. Ihre Angaben werden sicher gespeichert und vertraulich behandelt.</p>
              <div className="contact-list">
                <div><span>@</span><p><small>E-Mail</small><strong><a href="mailto:info@checkin-tarife.de" data-testid="contact-email-link">info@checkin-tarife.de</a></strong>
                  <em><span className={`channel-flag ${config.email_enabled ? "on" : "off"}`}>{config.email_enabled ? "Aktiv" : "Noch nicht aktiviert"}</span></em></p></div>
                <div><span>Tel</span><p><small>Telefon</small><strong><a href={PHONE_TEL} data-testid="contact-phone-link">{PHONE_DISPLAY}</a></strong>
                  <em>Mo–Fr, ganztägig & abends</em></p></div>
                <div><span>WA</span><p><small>WhatsApp</small><strong><a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link">Nachricht via WhatsApp</a></strong>
                  <em>{WHATSAPP_LABEL}</em></p></div>
                <div><span>⌂</span><p><small>Geschäftsanschrift</small><strong>An der Alten Ziegelei 5<br />48157 Münster</strong></p></div>
              </div>
            </div>
            <form className="callback-card reveal" id="callback-form" onSubmit={submitCallback}>
              <span className="form-chip">Rückruf vormerken</span>
              {cbDone ? (
                <div data-testid="callback-success">
                  <h3>Vielen Dank!</h3>
                  <p style={{ color: "var(--muted)", margin: 0 }}>
                    Ihre Rückruf-Anfrage ist bei uns eingegangen. Wir melden uns zur gewünschten Zeit bei Ihnen.
                  </p>
                </div>
              ) : (
                <>
                  <h3>Wann erreichen wir Sie am besten?</h3>
                  <input type="text" value={cb.company_hp || ""} onChange={(e) => setCbField("company_hp", e.target.value)}
                    style={{ display: "none" }} tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <label>Name *<input autoComplete="name" placeholder="Vor- und Nachname" value={cb.name || ""}
                    onChange={(e) => setCbField("name", e.target.value)} disabled={!config.callback_enabled} data-testid="callback-name" /></label>
                  <label>Telefon *<input type="tel" autoComplete="tel" placeholder="Telefonnummer" value={cb.phone || ""}
                    onChange={(e) => setCbField("phone", e.target.value)} disabled={!config.callback_enabled} data-testid="callback-phone" /></label>
                  <label>E-Mail (freiwillig)<input type="email" autoComplete="email" placeholder="E-Mail-Adresse" value={cb.email || ""}
                    onChange={(e) => setCbField("email", e.target.value)} disabled={!config.callback_enabled} data-testid="callback-email" /></label>
                  <label>Anliegen<select value={cb.topic || ""} onChange={(e) => setCbField("topic", e.target.value)}
                    disabled={!config.callback_enabled} data-testid="callback-topic">
                    <option value="">Bitte wählen</option><option>Strom</option><option>Gas</option>
                    <option>Internet</option><option>Mobilfunk</option><option>Gewerbe</option><option>Allgemein</option>
                  </select></label>
                  <label>Kontaktzeit *<select value={cb.preferred_time || ""} onChange={(e) => setCbField("preferred_time", e.target.value)}
                    disabled={!config.callback_enabled} data-testid="callback-time">
                    <option value="">Bevorzugte Zeit</option><option>08:00–10:00 Uhr</option><option>10:00–12:00 Uhr</option>
                    <option>12:00–15:00 Uhr</option><option>15:00–18:00 Uhr</option><option>18:00–20:00 Uhr</option>
                  </select></label>
                  <label className="check-row" style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                    <input type="checkbox" checked={!!cb.consent} onChange={(e) => setCbField("consent", e.target.checked)}
                      disabled={!config.callback_enabled} data-testid="callback-consent" />
                    <span>Ich bitte um die Bearbeitung meiner Rückruf-Anfrage. Die <Link to="/datenschutz" target="_blank" rel="noopener noreferrer" data-testid="callback-privacy-link">Datenschutzhinweise</Link> habe ich zur Kenntnis genommen. *</span>
                  </label>
                  <button className="button button-coral button-full" type="submit" disabled={!config.callback_enabled || cbBusy} data-testid="callback-submit-btn">
                    {!config.callback_enabled ? "Wird nach Einrichtung aktiviert" : cbBusy ? "Wird gesendet…" : "Rückruf anfragen"}
                  </button>
                  {cbMsg && <div className="form-message" role="alert" data-testid="callback-message">{cbMsg}</div>}
                  <small>Telefonischer Rückruf, Montag bis Freitag, ganztägig und abends.</small>
                </>
              )}
            </form>
          </div>
        </section>

        {/* FAQ */}
        <section className="section-pad faq">
          <div className="shell faq-grid">
            <div className="reveal"><span className="kicker">Kurz erklärt</span><h2>Häufige Fragen</h2><p>Was der Pre-Check kann – und was bewusst erst im persönlichen Gespräch geklärt wird.</p></div>
            <div className="accordion reveal">
              <details><summary>Ist der Pre-Check wirklich kostenlos?<span>+</span></summary><p>Ja. Für Kunden ist der Pre-Check kostenlos. Check'in Tarife erhält bei erfolgreicher Vermittlung eine Vergütung vom jeweiligen Anbieter.</p></details>
              <details><summary>Ist das Ergebnis bereits ein Tarifangebot?<span>+</span></summary><p>Nein. Das Ergebnis ist eine unverbindliche Orientierung. Konditionen, Verfügbarkeit und tatsächliche Ersparnis werden anschließend individuell geprüft.</p></details>
              <details><summary>Warum sehe ich noch keinen konkreten Anbieter?<span>+</span></summary><p>Die Ampel zeigt zunächst nur, ob eine genauere Prüfung sinnvoll wirken könnte. Eine Empfehlung aus den ausgewählten Partnerangeboten erfolgt im Beratungsgespräch.</p></details>
              <details><summary>Berät Check'in Tarife deutschlandweit?<span>+</span></summary><p>Ja. Privat- und Gewerbekunden können deutschlandweit beraten werden.</p></details>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="shell footer-main">
          <div className="footer-brand"><img src={LOGO} alt="Check'in Tarife" onClick={handleLogoClick} data-testid="footer-logo" style={{ cursor: "default" }} /><p>Tarife einchecken. Kosten auschecken.</p></div>
          <div><strong>Tarifwelten</strong><a href="#tarifwelten">Strom & Gas</a><a href="#tarifwelten">Internet & Glasfaser</a><a href="#tarifwelten">Mobilfunk</a></div>
          <div><strong>Unternehmen</strong><a href="#gewerbe">Gewerbekunden</a><a href="#kontakt">Kontakt</a></div>
          <div><strong>Rechtliches</strong>
            <Link className="footer-link" to="/impressum" data-testid="footer-imprint-link">Impressum</Link>
            <Link className="footer-link" to="/datenschutz" data-testid="footer-privacy-link">Datenschutz</Link>
          </div>
        </div>
        <div className="shell footer-bottom"><span>© {new Date().getFullYear()} Check'in Tarife GbR</span><span>Für Kunden kostenlos · ausgewählte Anbieter</span></div>
      </footer>
    </>
  );
}
